'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyBottomBar from '@/components/StickyBottomBar';
import { Shield, ChevronRight, ExternalLink, Filter, AlertTriangle, Clock, CheckCircle, FileText } from 'lucide-react';

type GuidanceStatus = 'Final' | 'Proposed' | 'Under Review';

interface GuidanceItem {
  id: string;
  date: string;
  title: string;
  status: GuidanceStatus;
  summary: string;
  articleSlug?: string;
  externalUrl?: string;
}

const guidanceItems: GuidanceItem[] = [
{
  id: 'guidance-001',
  date: 'May 29, 2026',
  title: 'Import Alert 66-40 Expansion — 38 Foreign Peptide API Manufacturers',
  status: 'Final',
  summary:
  'FDA expanded Import Alert 66-40 to cover 38 additional foreign manufacturers of peptide APIs in China, India, and South Korea. Facilities subject to automatic detention without physical examination. Affects an estimated 30% of injectable peptide API supply entering the U.S. annually.',
  articleSlug: 'fda-import-alert-66-40-peptide-apis'
},
{
  id: 'guidance-002',
  date: 'Mar 15, 2026',
  title: 'Compounded Peptide Labeling Requirements Update',
  status: 'Final',
  summary:
  'Updated labeling requirements for compounded peptide preparations, clarifying required disclosure language, prohibited claims, and documentation standards for 503A and 503B facilities. Effective immediately upon publication.',
  articleSlug: 'compounding-pharmacy-coa-requirements'
},
{
  id: 'guidance-003',
  date: 'Jan 8, 2026',
  title: '503B Outsourcing Facility Inspection Standards — Revised Framework',
  status: 'Under Review',
  summary:
  'Proposed revision to 503B outsourcing facility inspection standards introducing expanded supplier qualification documentation requirements. Comment period open through April 2026. Expected effective date Q3 2026 pending finalization.',
  articleSlug: '503b-outsourcing-compliance-update'
},
{
  id: 'guidance-004',
  date: 'Nov 20, 2025',
  title: 'Peptide API Certificate of Analysis Verification Guidance',
  status: 'Final',
  summary:
  'Final guidance clarifying FDA expectations for CoA verification practices at compounding facilities. Specifies acceptable third-party testing methodologies, documentation retention requirements, and supplier re-qualification timelines.',
  articleSlug: 'coa-verification-standards-2026'
},
{
  id: 'guidance-005',
  date: 'Sep 5, 2025',
  title: 'Draft Guidance: Manufacturing Standards for Injectable Peptides',
  status: 'Proposed',
  summary:
  'Draft guidance outlining updated manufacturing standards for injectable peptide preparations, including sterility assurance requirements, environmental monitoring, and container closure integrity testing. Comment period open through December 2025.'
},
{
  id: 'guidance-006',
  date: 'Jul 12, 2025',
  title: 'Import Safety Program — Peptide API Quality Standards',
  status: 'Under Review',
  summary:
  'Expanded import safety program targeting peptide API quality standards for foreign manufacturers. Framework under review following significant increase in adverse event reports linked to compounded injectable peptides in Q3 2025.'
},
{
  id: 'guidance-007',
  date: 'Apr 30, 2025',
  title: 'Compounding Pharmacy Compliance Framework Update',
  status: 'Final',
  summary:
  'Comprehensive update to the compounding pharmacy compliance framework addressing documentation requirements, facility registration standards, and adverse event reporting obligations. Supersedes 2022 framework guidance.'
},
{
  id: 'guidance-008',
  date: 'Feb 14, 2025',
  title: 'Bulk Drug Substances Nomination Process for Peptides',
  status: 'Proposed',
  summary:
  'Proposed update to the bulk drug substance nomination process for peptide compounds, including revised criteria for inclusion on the 503B bulks list and new clinical need documentation requirements. Public comment period open through May 2025.'
}];


const statusConfig: Record<GuidanceStatus, {label: string;className: string;icon: React.ElementType;}> = {
  Final: {
    label: 'Final',
    className: 'status-final',
    icon: CheckCircle
  },
  Proposed: {
    label: 'Proposed',
    className: 'status-proposed',
    icon: FileText
  },
  'Under Review': {
    label: 'Under Review',
    className: 'status-under-review',
    icon: Clock
  }
};

type FilterTab = 'All' | GuidanceStatus;
const filterTabs: FilterTab[] = ['All', 'Final', 'Proposed', 'Under Review'];

export default function FDATrackerContent() {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('All');

  const filtered = activeFilter === 'All' ?
  guidanceItems :
  guidanceItems.filter((g) => g.status === activeFilter);

  const counts: Record<FilterTab, number> = {
    All: guidanceItems.length,
    Final: guidanceItems.filter((g) => g.status === 'Final').length,
    Proposed: guidanceItems.filter((g) => g.status === 'Proposed').length,
    'Under Review': guidanceItems.filter((g) => g.status === 'Under Review').length
  };

  return (
    <>
      <Header />
      <main className="pb-20">
        {/* Hero */}
        <div className="relative h-[340px] lg:h-[420px] overflow-hidden">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1f0a8fb3e-1786579930305.png"
            alt="Government regulatory building representing FDA guidance and oversight of pharmaceutical industry"
            fill
            priority
            sizes="100vw"
            className="object-cover" />
          
          <div className="absolute inset-0 hero-gradient" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 pb-10 w-full">
              <div className="flex items-center gap-2 mb-2">
                <Shield size={16} className="text-primary" />
                <span className="section-eyebrow text-white/80">Regulatory Intelligence</span>
              </div>
              <h1 className="text-hero-md font-800 text-white leading-tight">FDA Guidance Tracker</h1>
              <p className="mt-2 text-white/80 text-base max-w-2xl">
                Tracking compounded peptide labeling, manufacturing, and import guidance from the U.S. Food and Drug Administration. Updated as new guidance is issued.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
          {/* Breadcrumb */}
          <nav className="py-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link href="/" className="hover:text-primary transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-foreground">FDA Guidance Tracker</span>
          </nav>

          {/* Alert banner */}
          <div className="mb-6 flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
            <AlertTriangle size={18} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-700 text-foreground mb-0.5">
                Active Alert: Import Alert 66-40 Expansion (May 29, 2026)
              </p>
              <p className="text-sm text-secondary-foreground">
                38 foreign peptide API manufacturers added to Import Alert 66-40. Facilities subject to automatic detention.{' '}
                <Link
                  href="/article-detail-page?slug=fda-import-alert-66-40-peptide-apis"
                  className="text-primary font-600 hover:underline">
                  
                  Read full analysis →
                </Link>
              </p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-1 border-b border-border pb-0 mb-6 overflow-x-auto">
            <div className="flex items-center gap-1 mr-2 text-muted-foreground flex-shrink-0">
              <Filter size={14} />
              <span className="text-xs font-600 uppercase tracking-wide">Filter:</span>
            </div>
            {filterTabs.map((tab) =>
            <button
              key={`filter-tab-${tab}`}
              onClick={() => setActiveFilter(tab)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-600 border-b-2 transition-colors whitespace-nowrap -mb-px ${
              activeFilter === tab ?
              'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`
              }>
              
                {tab}
                <span
                className={`text-xs px-1.5 py-0.5 rounded-full font-700 tabular-nums ${
                activeFilter === tab ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`
                }>
                
                  {counts[tab]}
                </span>
              </button>
            )}
          </div>

          {/* Guidance items */}
          <div className="space-y-4 pb-10">
            {filtered.map((item, idx) => {
              const config = statusConfig[item.status];
              const StatusIcon = config.icon;
              const isFirst = idx === 0 && activeFilter === 'All';

              return (
                <div
                  key={item.id}
                  className={`bg-card border rounded-xl p-5 lg:p-6 ${
                  isFirst ? 'border-primary/30 bg-red-50/30' : 'border-border'} card-hover`
                  }>
                  
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Date column */}
                    <div className="flex-shrink-0 lg:w-32">
                      <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide">
                        {item.date}
                      </p>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3 mb-3">
                        <h3 className="text-base font-700 text-foreground leading-snug flex-1">
                          {item.title}
                        </h3>
                        <span
                          className={`${config.className} text-xs font-700 px-2.5 py-1 rounded-full flex items-center gap-1 flex-shrink-0 self-start`}>
                          
                          <StatusIcon size={12} />
                          {config.label}
                        </span>
                      </div>

                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {item.summary}
                      </p>

                      <div className="flex items-center gap-3">
                        {item.articleSlug ?
                        <Link
                          href={`/article-detail-page?slug=${item.articleSlug}`}
                          className="flex items-center gap-1.5 text-sm font-700 text-primary hover:text-primary-dark transition-colors">
                          
                            Read Full Analysis <ChevronRight size={14} />
                          </Link> :

                        <a
                          href="https://www.fda.gov/drugs/guidance-compliance-regulatory-information"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-sm font-700 text-primary hover:text-primary-dark transition-colors">
                          
                            Read Full Guidance <ExternalLink size={13} />
                          </a>
                        }

                        {isFirst &&
                        <span className="tag-hot text-xs font-700 px-2 py-0.5 rounded uppercase tracking-wide">
                            ACTIVE ALERT
                          </span>
                        }
                      </div>
                    </div>
                  </div>
                </div>);

            })}
          </div>

          {/* Status legend */}
          <div className="border-t border-border pt-6 pb-4">
            <p className="text-xs font-700 uppercase tracking-wide text-muted-foreground mb-3">Status Key</p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="status-final text-xs font-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <CheckCircle size={12} /> Final
                </span>
                <span className="text-xs text-muted-foreground">Guidance is in effect</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="status-proposed text-xs font-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <FileText size={12} /> Proposed
                </span>
                <span className="text-xs text-muted-foreground">Draft / comment period open</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="status-under-review text-xs font-700 px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Clock size={12} /> Under Review
                </span>
                <span className="text-xs text-muted-foreground">Comment period closed, finalization pending</span>
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Guidance items tracked by USA Peptide Pros editorial team. For official FDA guidance documents, visit{' '}
              <a
                href="https://www.fda.gov"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-600">
                
                fda.gov
              </a>
              . This tracker is for informational purposes only and does not constitute legal or compliance advice.
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <StickyBottomBar />
    </>);

}